import React from "react";
import { getProgressPoint } from "../../../helpers/bar-helper";
import { BarDisplay } from "./bar-display";
import { BarDateHandle } from "./bar-date-handle";
import { BarProgressHandle } from "./bar-progress-handle";
import { TaskItemProps } from "../task-item";
import styles from "./bar.module.css";

export const Bar: React.FC<TaskItemProps> = ({
  task,
  isProgressChangeable,
  isDateChangeable,
  rtl,
  type,
  onEventStart,
  isSelected,
}) => {
  const progressPoint = getProgressPoint(
    +!rtl * task.progressWidth + task.progressX,
    task.y,
    task.height
  );
  const handleHeight = task.height / 2 - 1;

  // When segments exist (calendar with off-days/holidays), snap handles to the
  // nearest colored-bar boundary so they remain visible and usable even when the
  // task's declared start/end dates fall on off-days.
  const plannedSegs = task.plannedSegments;
  const plannedLeftHandleX = plannedSegs?.length
    ? plannedSegs[0].x1
    : task.x1;
  const plannedRightHandleX = plannedSegs?.length
    ? plannedSegs[plannedSegs.length - 1].x2
    : task.x2;

  const actualSegs = task.actualSegments;
  const actualLeftHandleX = actualSegs?.length
    ? actualSegs[0].x1
    : task.actualx1;
  const actualRightHandleX = actualSegs?.length
    ? actualSegs[actualSegs.length - 1].x2
    : task.actualx2;

  if (type == "planned") {
    if (task?.x1 >= 0 && task?.x2 >= 0 && task.x2 - task.x1 >= 0)
      return (
        <g className={styles.barWrapper} tabIndex={0}>
          {/* Transparent hit area ensures hover stays active across segment gaps */}
          <rect x={task.x1} y={task.y} width={task.x2 - task.x1} height={task.height / 2} fill="transparent" />
          <BarDisplay
            x={task.x1}
            y={task.y}
            type={type as string}
            startProgressWidth={task.progressStartWidth}
            endProgressWidth={task.progressEndWidth}
            width={task.x2 - task.x1}
            height={task.height / 2}
            progressX={task.progressX}
            progressWidth={task.progressWidth}
            barCornerRadius={task.barCornerRadius}
            styles={task.styles}
            isSelected={isSelected}
            segments={task.plannedSegments}
            onMouseDown={e => {
              isDateChangeable && onEventStart("move", task, e, "planned");
            }}
          />
          <g className="handleGroup">
            {isDateChangeable && (
              <g>
                {/* left — anchored to first segment so it stays on the colored bar
                    even when the declared start date falls on an off-day/holiday */}
                <BarDateHandle
                  x={plannedLeftHandleX + 1}
                  y={task.y + 1}
                  width={task.handleWidth}
                  height={handleHeight}
                  barCornerRadius={task.barCornerRadius}
                  onMouseDown={e => {
                    onEventStart("start", task, e, "planned");
                  }}
                />
                {/* right — anchored to last segment */}
                <BarDateHandle
                  x={plannedRightHandleX - task.handleWidth - 1}
                  y={task.y + 1}
                  width={task.handleWidth}
                  height={handleHeight}
                  barCornerRadius={task.barCornerRadius}
                  onMouseDown={e => {
                    onEventStart("end", task, e, "planned");
                  }}
                />
              </g>
            )}
            {isProgressChangeable && (
              <BarProgressHandle
                progressPoint={progressPoint}
                onMouseDown={e => {
                  onEventStart("progress", task, e, "planned");
                }}
              />
            )}
          </g>
        </g>
      );
    else return <g className={styles.barWrapper} tabIndex={0}></g>;
  } else if (task?.actualx1 >= 0 && task?.actualx2 >= 0 && task.actualx2 - task.actualx1 >= 0) {
    return (
      <g className={styles.barWrapper} tabIndex={0}>
        {/* Transparent hit area ensures hover stays active across segment gaps */}
        <rect x={task.actualx1} y={task.y + task.height / 2} width={task.actualx2 - task.actualx1} height={task.height / 2} fill="transparent" />
        <BarDisplay
          x={task.actualx1}
          y={task.y + task.height / 2}
          type={type as string}
          startProgressWidth={task.progressStartWidth}
          endProgressWidth={task.progressEndWidth}
          width={task.actualx2 - task.actualx1}
          height={task.height / 2}
          progressX={task.progressX}
          progressWidth={task.progressWidth}
          barCornerRadius={task.barCornerRadius}
          styles={task.styles}
          isSelected={isSelected}
          segments={task.actualSegments}
          onMouseDown={e => {
            isDateChangeable && onEventStart("move", task, e, "actual");
          }}
        />
        <g className="handleGroup">
          {isDateChangeable && (
            <g>
              {/* left — anchored to first actual segment */}
              <BarDateHandle
                x={actualLeftHandleX + 1}
                y={task.y + task.height / 2 + 1}
                width={task.handleWidth}
                height={handleHeight}
                barCornerRadius={task.barCornerRadius}
                onMouseDown={e => {
                  onEventStart("start", task, e, "actual");
                }}
              />
              {/* right — anchored to last actual segment */}
              <BarDateHandle
                x={actualRightHandleX - task.handleWidth - 1}
                y={task.y + task.height / 2 + 1}
                width={task.handleWidth}
                height={handleHeight}
                barCornerRadius={task.barCornerRadius}
                onMouseDown={e => {
                  onEventStart("end", task, e, "actual");
                }}
              />
            </g>
          )}
          {isProgressChangeable && (
            <BarProgressHandle
              progressPoint={progressPoint}
              onMouseDown={e => {
                onEventStart("progress", task, e, "actual");
              }}
            />
          )}
        </g>
      </g>
    );
  } else {
    return <g className={styles.barWrapper} tabIndex={0}></g>;
  }
};
